import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Loader } from '../../reusable/components/loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { BlogSummary } from '../../models/BlogSummary';
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  limit, 
  startAfter, 
  where
} from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import './BlogPage.css';

// Optimized direct Firestore blog fetching
const fetchBlogsOptimized = async (limitCount = 6, startAfterDoc = null, category = '') => {
  try {
    // Build query with field selection for summary data only
    let blogsQuery = query(
      collection(db, 'blogs'),
      orderBy('date', 'desc'),
      limit(limitCount)
    );

    // Add category filtering
    if (category && category !== 'All') {
      blogsQuery = query(
        collection(db, 'blogs'),
        where('category', '==', category),
        orderBy('date', 'desc'),
        limit(limitCount)
      );
    }

    // Handle pagination
    if (startAfterDoc) {
      if (category && category !== 'All') {
        blogsQuery = query(
          collection(db, 'blogs'),
          where('category', '==', category),
          orderBy('date', 'desc'),
          startAfter(startAfterDoc),
          limit(limitCount)
        );
      } else {
        blogsQuery = query(
          collection(db, 'blogs'),
          orderBy('date', 'desc'),
          startAfter(startAfterDoc),
          limit(limitCount)
        );
      }
    }

    const snapshot = await getDocs(blogsQuery);
    
    // Map only summary fields to reduce data transfer
    const blogs = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '',
        summary: data.summary || '',
        image: data.image || '',
        date: data.date || null,
        author: data.author || '',
        readTime: data.readTime || '',
        category: data.category || ''
      };
    });

    return {
      blogs,
      lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error fetching blogs:', error);
    return { blogs: [], lastDoc: null, hasMore: false };
  }
};

// Optimized category fetching
const fetchCategoriesOptimized = async () => {
  try {
    const categoriesQuery = query(
      collection(db, 'blogs'),
      orderBy('category')
    );
    
    const snapshot = await getDocs(categoriesQuery);
    const uniqueCategories = new Set<string>();
    
    snapshot.docs.forEach(doc => {
      const category = doc.data().category;
      if (category && category.trim()) {
        uniqueCategories.add(category.trim());
      }
    });
    
    return ['All', ...Array.from(uniqueCategories).sort()];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return ['All'];
  }
};

interface BlogSummaryTileProps {
  blog: BlogSummary;
  onClick: () => void;
}

// Helper to format Firestore Timestamp or string date
function formatDate(date: any): string {
  if (!date) return '';
  // If ISO string (YYYY-MM-DD), format as '10 Mar 2025'
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (typeof date === 'string') return date;
  if (date._seconds) {
    return new Date(date._seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return date.toString();
}

// Blog summary tile UI component
const BlogSummaryTile: React.FC<BlogSummaryTileProps> = ({ blog, onClick }) => (
  <div className="blog-summary-tile" onClick={onClick}>
    <div className="blog-summary-image-wrapper">
      <img className="blog-summary-image" src={blog.image} alt={blog.category} loading="lazy" />
      <span className="blog-summary-category">{blog.category}</span>
    </div>
    <div className="blog-summary-content">
      <div className="blog-summary-title">{blog.title}</div>
      <div className="blog-summary-author">By {blog.author}</div>
      <svg className="blog-summary-divider" width="333" height="2" viewBox="0 0 333 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 1H333" stroke="#E5E7EB"/>
      </svg>
      <div className="blog-summary-meta">
        <span>{formatDate(blog.date)}</span>
        <span className="blog-summary-dot">•</span>
        <span>{blog.readTime}</span>
      </div>
    </div>
  </div>
);

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [blogSummaries, setBlogSummaries] = useState<BlogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [lastDoc, setLastDoc] = useState<any | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [categories, setCategories] = useState<string[]>(['All']);

  // Read category from URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  // Fetch unique categories efficiently
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryList = await fetchCategoriesOptimized();
        setCategories(categoryList);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to basic categories
        setCategories(['All']);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchBlogsOptimized(6, null, selectedCategory).then(({ blogs, hasMore, lastDoc }) => {
      setBlogSummaries(blogs);
      setHasMore(hasMore);
      setLastDoc(lastDoc);
      setLoading(false);
    });
  }, [selectedCategory]);

  // Handler for loading more blogs
  const loadMoreBlogs = async () => {
    setLoading(true);
    const { blogs, lastDoc: newLastDoc, hasMore: newHasMore } = await fetchBlogsOptimized(6, lastDoc, selectedCategory);
    setBlogSummaries(prev => [...prev, ...blogs]);
    setHasMore(newHasMore);
    setLastDoc(newLastDoc);
    setLoading(false);
  };

  // Handler for category selection
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setBlogSummaries([]);
    setLastDoc(null);
    
    // Update URL to reflect selected category
    if (category === 'All') {
      navigate('/blogs', { replace: true });
    } else {
      navigate(`/blogs?category=${encodeURIComponent(category)}`, { replace: true });
    }
  };

  const tilesInRow = 3;
  const remainder = blogSummaries.length % tilesInRow;
  const fillers = remainder === 0 ? 0 : tilesInRow - remainder;

  return (
    <div>
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{selectedCategory === 'All' ? 'Design Blog - Thinking in Space | Kydoscope' : `${selectedCategory} Articles | Kydoscope Blog`}</title>
        <meta 
          name="description" 
          content={selectedCategory === 'All' 
            ? "Explore interior design ideas, trends, and experiences redefining interiors and built environments. Get inspired by expert insights on Kydoscope design blog."
            : `Discover ${selectedCategory} articles and insights. Expert tips and trends for interior design and architecture on Kydoscope blog.`
          } 
        />
        <meta 
          name="keywords" 
          content={selectedCategory === 'All'
            ? "interior design blog, architecture articles, design trends, space planning, design inspiration, home decor"
            : `${selectedCategory}, interior design, architecture, design blog, ${selectedCategory} tips, design trends`
          }
        />
        
        {/* Open Graph Meta Tags */}
        <meta 
          property="og:title" 
          content={selectedCategory === 'All' ? 'Design Blog - Thinking in Space | Kydoscope' : `${selectedCategory} Articles | Kydoscope Blog`} 
        />
        <meta 
          property="og:description" 
          content={selectedCategory === 'All'
            ? "Explore interior design ideas, trends, and experiences redefining interiors and built environments."
            : `Discover ${selectedCategory} articles and expert insights on interior design and architecture.`
          }
        />
        <meta property="og:type" content="website" />
        <meta 
          property="og:url" 
          content={selectedCategory === 'All' ? 'https://kydoscope.com/blogs' : `https://kydoscope.com/blogs?category=${encodeURIComponent(selectedCategory)}`} 
        />
        <meta property="og:image" content="https://kydoscope.com/logo512.png" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta 
          name="twitter:title" 
          content={selectedCategory === 'All' ? 'Design Blog - Thinking in Space | Kydoscope' : `${selectedCategory} Articles | Kydoscope Blog`} 
        />
        <meta 
          name="twitter:description" 
          content={selectedCategory === 'All'
            ? "Explore interior design ideas, trends, and experiences redefining interiors."
            : `Discover ${selectedCategory} articles and expert design insights.`
          }
        />
        
        {/* Additional SEO */}
        <link 
          rel="canonical" 
          href={selectedCategory === 'All' ? 'https://kydoscope.com/blogs' : `https://kydoscope.com/blogs?category=${encodeURIComponent(selectedCategory)}`}
        />
        <meta name="robots" content="index, follow" />
        
        {/* Structured Data for Blog Collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": selectedCategory === 'All' ? "Kydoscope Design Blog" : `${selectedCategory} Articles - Kydoscope Blog`,
            "description": selectedCategory === 'All' 
              ? "Interior design ideas, trends, and experiences redefining interiors and built environments."
              : `${selectedCategory} articles and insights for interior design and architecture.`,
            "url": selectedCategory === 'All' ? 'https://kydoscope.com/blogs' : `https://kydoscope.com/blogs?category=${encodeURIComponent(selectedCategory)}`,
            "publisher": {
              "@type": "Organization",
              "name": "Kydoscope",
              "logo": {
                "@type": "ImageObject",
                "url": "https://kydoscope.com/logo512.png"
              }
            },
            "blogPost": blogSummaries.slice(0, 5).map(blog => ({
              "@type": "BlogPosting",
              "headline": blog.title,
              "description": blog.summary,
              "image": blog.image,
              "author": {
                "@type": "Person",
                "name": blog.author
              },
              "datePublished": blog.date,
              "url": `https://kydoscope.com/blogs/${blog.id}`,
              "articleSection": blog.category
            }))
          })}
        </script>
      </Helmet>

      <div className="blog-page-header">
        <h1 className="blog-page-title">Design Insights & Inspiration</h1>
        <div className="blog-page-subhead">
          Expert perspectives and fresh ideas for creating spaces that truly work for your lifestyle.
        </div>
      </div>
      <div className="blog-page-divider"></div>
      
      {/* Categories List */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <ul className="blog-categories-list">
          {categories.map(category => (
            <li key={category} className="blog-category-list-item">
              <button
                className={`blog-category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategorySelect(category)}
                style={{
                  background: selectedCategory === category ? '#1F2937' : '#F9FAFB',
                  color: selectedCategory === category ? '#fff' : '#1F2937',
                  border: selectedCategory === category ? '1px solid #1F2937' : '1px solid #E5E7EB'
                }}
              >
                {category}
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="blog-tiles-wrapper">
        {blogSummaries.map((blog) => (
          <BlogSummaryTile
            key={blog.id}
            blog={blog}
            onClick={() => navigate(`/blogs/${blog.id}`)}
          />
        ))}
        {Array.from({ length: fillers }).map((_, idx) => (
          <div key={`filler-${idx}`} className="blog-summary-tile invisible" />
        ))}
        {loading && (
          <div
            className="blog-grid-full-row"
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '200px', color: '#1F2937' }}
          >
            <div style={{ marginBottom: '18px', fontSize: '1.15rem', color: '#444', fontWeight: 500 }}>
              Curating design inspiration for you
            </div>
            <Loader />
          </div>
        )}
        {!loading && hasMore && (
          <div className="blog-grid-full-row" style={{ textAlign: 'center', margin: '32px auto' }}>
            <button
              className="blog-page-btn"
              onClick={loadMoreBlogs}
              style={{ width: 200, height: 48, fontWeight: 500, borderRadius: '12px', border: 'none', background: '#1F2937', fontSize: '14px', color: '#fff' }}
            >
              Read more blogs
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
